import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { sparkActivityApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import type { SparkActivityFilters, SparkActivityStatus, UpdateSparkActivityPayload, ResolveMismatchPayload, CreateSparkActivityPayload } from '../types/spark-activity';

export function useSparkActivities(filters: Partial<SparkActivityFilters>) {
  return useQuery({
    queryKey: queryKeys.sparkActivities.list(filters as Record<string, unknown>),
    queryFn: () => sparkActivityApi.listActivities(filters),
  });
}

export function useSparkActivity(id: number | null) {
  return useQuery({
    queryKey: queryKeys.sparkActivities.detail(id ?? 0),
    queryFn: () => sparkActivityApi.getActivity(id!),
    enabled: id !== null,
  });
}

export function useSparkActivityBatches() {
  return useQuery({
    queryKey: queryKeys.sparkActivities.batches(),
    queryFn: () => sparkActivityApi.getBatches(),
  });
}

export function useSparkActivityFilterOptions() {
  return useQuery({
    queryKey: queryKeys.sparkActivities.filters(),
    queryFn: () => sparkActivityApi.getFilterOptions(),
  });
}

export function useUpdateSparkActivityStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      sparkActivityApi.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
      const messageMap: Partial<Record<SparkActivityStatus, string>> = {
        published: 'Activity is now live',
        unpublished: 'Activity unpublished',
        archived: 'Activity archived',
      };
      toast.success(messageMap[variables.status as SparkActivityStatus] ?? 'Status updated');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update status');
    },
  });
}

export function useUpdateSparkActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSparkActivityPayload }) =>
      sparkActivityApi.updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
      toast.success('Activity saved');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to save activity');
    },
  });
}

export function useUploadSparkActivityCsv() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => sparkActivityApi.uploadCsv(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Upload failed');
    },
  });
}

export function useResolveMismatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ activityId, data }: { activityId: number; data: ResolveMismatchPayload }) =>
      sparkActivityApi.resolveMismatch(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
      toast.success('Mismatch resolved');
    },
    onError: () => toast.error('Failed to resolve mismatch'),
  });
}

export function useCreateSparkActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSparkActivityPayload) => sparkActivityApi.createActivity(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create activity');
    },
  });
}

export function useDeleteSparkActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => sparkActivityApi.deleteActivity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
      toast.success('Activity deleted');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to delete activity');
    },
  });
}

export function useDeleteSparkActivityBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: number) => sparkActivityApi.deleteBatch(batchId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparkActivities.all });
      toast.success(`${data.deletedCount} activities deleted`);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to delete batch');
    },
  });
}

export function useCheckActivityKey(key: string) {
  return useQuery({
    queryKey: queryKeys.sparkActivities.checkKey(key),
    queryFn: () => sparkActivityApi.checkKeyAvailability(key),
    enabled: key.trim().length > 0,
    staleTime: 5000,
  });
}

export function useDownloadSparkActivityCsv() {
  return useMutation({
    mutationFn: () => sparkActivityApi.downloadCsv(),
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to download CSV');
    },
  });
}
